import {
    MainPageInfoService,
    VehicleInfoService,
} from '../../../epics/common/commonData';
import {
    cityKey,
    mainStatsKey,
    regionKey,
    stateKey,
    typeCorporateKey,
    advertsCountKey,
    yearsKey,
    moderateTextKey,
    reviewVehicleKey,
    aiDescriptionKey,
    vehicleOptionsKey,
    fuelPricesKey,
    translateTextKey,
} from '../thunkKeys/commonInfoKeys';
import { setCurrencyRates } from '../common_reducers/currencyRates';
import { createAsyncThunkWithCaching } from '../../thunkWrapper';
import { createAsyncThunk } from '@reduxjs/toolkit';

export const translateText = createAsyncThunk(
    translateTextKey,
    async ({ text, targetLang, cacheKey }, { rejectWithValue }) => {
        try {
            const response = await VehicleInfoService.translateText(
                text,
                targetLang,
            );
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || 'Error translating text',
            );
        }
    },
);

export const getFuelPrices = createAsyncThunk(
    fuelPricesKey,
    async (_, { rejectWithValue }) => {
        try {
            const response = await VehicleInfoService.getFuelPrices();
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || 'Error fetching fuel prices',
            );
        }
    },
);

export const getVehicleReview = createAsyncThunk(
    reviewVehicleKey,
    async ({ vehicleData, lang }, { rejectWithValue }) => {
        try {
            const response = await VehicleInfoService.getVehicleReview(
                vehicleData,
                lang,
            );
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || 'Error fetching review',
            );
        }
    },
);

export const generateAiDescription = createAsyncThunk(
    aiDescriptionKey,
    async ({ vehicleData, lang }, { rejectWithValue }) => {
        try {
            const response = await VehicleInfoService.generateAiDescription(
                vehicleData,
                lang,
            );
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'aiDescriptionError',
            );
        }
    },
);

export const getVehicleOptions = createAsyncThunk(
    vehicleOptionsKey,
    async ({ vehicleData, availableOptions }, { rejectWithValue }) => {
        try {
            const response = await VehicleInfoService.getVehicleOptions(
                vehicleData,
                availableOptions,
            );
            return response || {};
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'vehicleOptionsError',
            );
        }
    },
);

export const moderateText = createAsyncThunk(
    moderateTextKey,
    async ({ text, fieldName }, thunkAPI) => {
        try {
            const response = await VehicleInfoService.moderateText(text);
            return {
                ...response,
                fieldName,
                text,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error.message,
                fieldName,
            });
        }
    },
);

export const getAdvertsListCount = createAsyncThunk(
    advertsCountKey,
    async (payload, thunkAPI) => {
        try {
            const response =
                await VehicleInfoService.getAdvertsListCount(payload);
            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    },
);

export const getCity = createAsyncThunkWithCaching(
    cityKey,
    async (_, thunkApi) => {
        try {
            const response = await VehicleInfoService.getCity();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getYears = createAsyncThunkWithCaching(
    yearsKey,
    async (_, thunkApi) => {
        try {
            const response = await VehicleInfoService.getYears();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getRegion = createAsyncThunkWithCaching(
    regionKey,
    async (_, thunkApi) => {
        try {
            const response = await VehicleInfoService.getRegion();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getTypeCorporate = createAsyncThunkWithCaching(
    typeCorporateKey,
    async (_, thunkApi) => {
        try {
            const response = await VehicleInfoService.getTypeCorporate();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getState = createAsyncThunkWithCaching(
    stateKey,
    async (_, thunkApi) => {
        try {
            const response = await VehicleInfoService.getState();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

export const getMainPageStats = createAsyncThunkWithCaching(
    mainStatsKey,
    async (_, thunkApi) => {
        try {
            const response = await MainPageInfoService.getMainStats();
            return response;
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);

const exchangeUrl =
    'https://nbg.gov.ge/gw/api/ct/monetarypolicy/currencies/ka/json/';

const GEL_RATE_CODES = ['USD', 'EUR', 'KZT', 'RUB'];

export const getCurrencyRates = createAsyncThunkWithCaching(
    'common/getCurrencyRates',
    async (_, thunkApi) => {
        try {
            const response = await fetch(exchangeUrl);
            const data = await response.json();
            const currencies = data[0]?.currencies || [];

            const usdRate =
                currencies.find((currency) => currency.code === 'USD')?.rate ||
                1;
            const eurRate =
                currencies.find((currency) => currency.code === 'EUR')?.rate ||
                1;
            const gel = Object.fromEntries(
                currencies
                    .filter(({ code }) => GEL_RATE_CODES.includes(code))
                    .map(({ code, rate, quantity }) => [
                        code,
                        rate / (quantity || 1),
                    ]),
            );

            return thunkApi.dispatch(
                setCurrencyRates({ usd: usdRate, eur: usdRate / eurRate, gel }),
            );
        } catch (error) {
            return thunkApi.rejectWithValue(error.message);
        }
    },
);
